import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Empty, Field, KV, StatusBadge } from "@/components/common";
import { FleetMap } from "@/components/map/FleetMap";
import { QuickActions } from "@/components/QuickActions";
import { VehicleInfoPanel } from "@/components/VehicleInfoPanel";
import { EngineCommand } from "@/components/EngineCommand";
import { EventItem } from "@/components/EventItem";
import { VehicleTypeIcon } from "@/components/VehicleRow";
import { HistoryView } from "@/pages/History";
import { PlaybackView } from "@/pages/Playback";
import { useFleet } from "@/context/FleetContext";
import { api, errMsg } from "@/lib/api";
import { dateInput, daysAgo, fmtDateTime, fmtDuration, timeAgo, toIso, vehicleStatus, isGpsFresh } from "@/lib/fleet";

function Overview({ v, d }) {
  const st = vehicleStatus(v);
  const [follow, setFollow] = useState(false);
  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_420px]">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3" data-testid="vehicle-overview">
        <KV label="Status kendaraan" value={<StatusBadge status={st} />} mono={false} />
        <KV label="Kecepatan" value={isGpsFresh(v) ? `${Math.round(v.speed || 0)} km/j` : "—"} testId="overview-speed" />
        <KV label="ACC" value={v.acc === true ? "ON" : v.acc === false ? "OFF" : "BELUM ADA DATA"} testId="overview-acc" />
        <KV label="Lokasi terakhir" value={isGpsFresh(v) ? `${v.lat?.toFixed(5)}, ${v.lng?.toFixed(5)}` : "—"} testId="overview-location" />
        <KV label="Jarak hari ini" value={`${d.today?.distance_km ?? 0} km`} testId="overview-distance" />
        <KV label="Odometer" value={`${Math.round(v.odometer || 0).toLocaleString("id-ID")} km`} />
        <KV label="Waktu update" value={`${fmtDateTime(v.server_time)}`} />
        <KV label="Status GPS" value={st === "offline" ? "Tidak ada sinyal" : `${v.gps_valid === false ? "No fix" : "Fix"} · ${v.satellites ?? "-"} sat · ±${v.accuracy ?? "-"}m`} />
        <KV label="Power / Baterai" value={v.power === true ? "Terhubung" : v.power === false ? "Terputus" : "BELUM ADA DATA"} />
        <KV label="Trip hari ini" value={`${d.today?.trips ?? 0} trip · ${fmtDuration(d.today?.moving_s || 0)}`} />
        <KV label="Kec. maks hari ini" value={`${Math.round(d.today?.max_speed || 0)} km/j`} />
        <KV label="Masa aktif" value={v.expires_at} />
        <KV label="Perangkat" value={`${d.device?.model || "-"} · ${d.device?.protocol === "arcoder" ? "Glazy" : d.device?.protocol || "-"}`} />
        <KV label="IMEI" value={v.imei} />
        <KV label="SIM" value={d.device?.sim_number} />
      </div>
      <div className="gt-card relative h-[320px] overflow-hidden lg:h-auto">
        <FleetMap
          vehicles={[v]}
          selectedId={v.id}
          follow={follow}
          className="h-full min-h-[320px]"
          testId="overview-map"
        />
        <button
          type="button"
          onClick={() => setFollow((value) => !value)}
          data-testid="overview-follow-toggle"
          className={`absolute z-[600] left-3 top-3 flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold backdrop-blur ${
            follow
              ? "border-primary bg-primary text-primary-foreground"
              : "border-border bg-card/90 text-foreground"
          }`}
        >
          {follow ? "Mengikuti" : "Ikuti kendaraan"}
        </button>
      </div>
    </div>
  );
}

function EventsTab({ vid }) {
  const [items, setItems] = useState(null);
  const load = () => api.get("/events", { params: { vehicle_id: vid, limit: 100 } }).then((r) => setItems(r.data.items));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [vid]);
  const ack = async (id) => { await api.post(`/events/${id}/ack`); load(); };
  return (
    <div className="gt-card p-2" data-testid="vehicle-events-tab">
      {(items || []).map((e) => <EventItem key={e.id} e={e} onAck={ack} />)}
      {items && !items.length && <Empty title="Belum ada event" />}
    </div>
  );
}

function ReportsTab({ vid }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/reports/mileage", { params: { vehicle_id: vid, start: toIso(dateInput(daysAgo(6))), end: toIso(dateInput(), "23:59") } }).then((r) => setRows(r.data.rows));
  }, [vid]);
  return (
    <div className="gt-card p-5" data-testid="vehicle-reports-tab">
      <div className="mb-1 text-sm font-bold">Jarak harian · 7 hari</div>
      <div className="mb-4 text-xs text-muted-foreground">Total {rows.reduce((a, r) => a + r.distance_km, 0).toFixed(1)} km · {rows.reduce((a, r) => a + r.trips, 0)} trip</div>
      <div className="h-[260px]">
        <ResponsiveContainer>
          <BarChart data={rows}>
            <XAxis dataKey="date" tickFormatter={(x) => x.slice(5)} stroke="hsl(var(--muted-foreground))" fontSize={11} />
            <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
            <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} />
            <Bar dataKey="distance_km" name="Jarak (km)" fill="#EAB308" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <Link to="/reports" className="mt-3 inline-block text-xs font-semibold gt-brand-text">Buka laporan lengkap →</Link>
    </div>
  );
}

function SettingsTab({ v, onSaved }) {
  const [f, setF] = useState({ name: v.name, plate: v.plate || "", driver: v.driver || "", speed_limit: v.speed_limit || 80, notify_movement_acc_off: v.notify_movement_acc_off === true });
  const [cmds, setCmds] = useState([]);
  const { commandTick } = useFleet();
  useEffect(() => { api.get(`/vehicles/${v.id}/commands`).then((r) => setCmds(r.data)); }, [v.id, v.immobilized, commandTick]);
  const save = async () => {
    try {
      await api.put(`/vehicles/${v.id}/settings`, { ...f, speed_limit: Number(f.speed_limit) });
      toast.success("Pengaturan kendaraan disimpan");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
      <div className="gt-card space-y-3 p-5" data-testid="vehicle-settings-form">
        <div className="text-sm font-bold">Pengaturan Kendaraan</div>
        <Field label="Nama kendaraan"><input className="gt-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="settings-vehicle-name" /></Field>
        <Field label="Plat nomor"><input className="gt-input" value={f.plate} onChange={(e) => setF({ ...f, plate: e.target.value.toUpperCase() })} data-testid="settings-vehicle-plate" /></Field>
        <Field label="Pengemudi"><input className="gt-input" value={f.driver} onChange={(e) => setF({ ...f, driver: e.target.value })} data-testid="settings-vehicle-driver" /></Field>
        <Field label="Batas kecepatan overspeed (km/j)"><input type="number" className="gt-input" value={f.speed_limit} onChange={(e) => setF({ ...f, speed_limit: e.target.value })} data-testid="settings-vehicle-speed-limit" /></Field>
        <label className="flex items-start gap-3 rounded-xl border border-border p-3 cursor-pointer">
          <input type="checkbox" className="mt-1 h-4 w-4 accent-primary" checked={f.notify_movement_acc_off} onChange={(e) => setF({ ...f, notify_movement_acc_off: e.target.checked })} data-testid="settings-notify-movement-acc-off" />
          <span><span className="block text-sm font-semibold">Notifikasi pergerakan saat ACC OFF</span><span className="mt-1 block text-xs text-muted-foreground">Aktifkan hanya jika kabel ACC kendaraan dipasang. Jika mati, peringatan “Pergerakan tanpa ACC” tidak dibuat untuk kendaraan ini.</span></span>
        </label>
        <button onClick={save} className="rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground" data-testid="settings-vehicle-save">Simpan</button>
      </div>

    </div>
  );
}

export default function VehicleDetail() {
  const { id } = useParams();
  const { byId, commandTick } = useFleet();
  const [cmds, setCmds] = useState([]);
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [liveFollow, setLiveFollow] = useState(true);
  const load = () => api.get(`/vehicles/${id}`).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e)));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);
  useEffect(() => {
    let active = true;
    api.get(`/vehicles/${id}/commands`)
      .then((r) => { if (active) setCmds(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (active) setCmds([]); });
    return () => { active = false; };
  }, [id, commandTick]);
  if (err) return <div className="gt-card"><Empty title="Kendaraan tidak ditemukan" text={err} /></div>;
  if (!d) return <div className="h-40 animate-pulse rounded-2xl bg-card" />;
  const v = { ...d, ...(byId[id] || {}) };
  const tabs = [["overview", "Overview"], ["live", "Live"], ["history", "History"], ["playback", "Playback"], ["events", "Events"], ["reports", "Reports"], ["settings", "Settings"]];
  return (
    <div data-testid="vehicle-detail-page">
      <Link to="/vehicles" className="mb-4 inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground" data-testid="vehicle-detail-back"><ArrowLeft className="h-3.5 w-3.5" /> Semua kendaraan</Link>
      <div className="gt-card mb-5 flex flex-col gap-5 p-5 lg:flex-row lg:items-center lg:justify-between gt-rise">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground"><VehicleTypeIcon type={v.type} className="h-7 w-7" /></div>
          <div>
            <div className="flex flex-wrap items-center gap-2"><h1 className="text-2xl font-extrabold tracking-tight" data-testid="vehicle-detail-name">{v.name}</h1><StatusBadge status={vehicleStatus(v)} testId="vehicle-detail-status" /></div>
            <div className="mt-0.5 font-mono text-sm text-muted-foreground">{v.plate} · IMEI {v.imei}{v.customer_name ? ` · ${v.customer_name}` : ""}</div>
          </div>
        </div>
        <QuickActions vehicleId={v.id} simNumber={d.device?.sim_number} className="w-full lg:w-[420px]" />
      </div>
      <Tabs defaultValue="overview">
        <TabsList className="gt-noscroll mb-5 h-auto w-full justify-start overflow-x-auto rounded-2xl border border-border bg-card p-1">
          {tabs.map(([k, l]) => <TabsTrigger key={k} value={k} className="rounded-xl px-4 py-2 text-[13px] font-semibold data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid={`vehicle-tab-${k}`}>{l}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="overview"><Overview v={v} d={d} /></TabsContent>
        <TabsContent value="live">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_360px]">
            <div className="gt-card relative h-[460px] overflow-hidden lg:h-[640px]">
              <FleetMap vehicles={[v]} selectedId={v.id} follow={liveFollow} className="h-full" testId="vehicle-live-map" />
              <button
                type="button"
                onClick={() => setLiveFollow((value) => !value)}
                data-testid="vehicle-live-follow-toggle"
                className={`absolute left-3 top-3 z-[600] rounded-full border px-3 py-1.5 text-xs font-bold backdrop-blur ${
                  liveFollow
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-card/90 text-foreground"
                }`}
              >
                {liveFollow ? "Mengikuti" : "Ikuti kendaraan"}
              </button>
            </div>
            <div className="space-y-4">
              <div className="gt-card"><VehicleInfoPanel v={v} showCommand={false} /></div>
              <EngineCommand vehicle={v} />
              <div className="gt-card p-4">
                <div className="mb-2 text-sm font-bold">Riwayat Perintah</div>
                {cmds.map((c) => (
                  <div key={c.id} className="flex items-center justify-between border-b border-border/60 py-2 text-xs last:border-0">
                    <span className="font-semibold">{c.command === "engine_cut" ? "Engine cut-off" : "Pulihkan mesin"} <span className="text-muted-foreground">· {c.requested_by}</span></span>
                    <span className="font-mono text-muted-foreground">{c.status} · {timeAgo(c.created_at)}</span>
                  </div>
                ))}
                {!cmds.length && <div className="text-xs text-muted-foreground">Belum ada perintah dikirim.</div>}
              </div>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="history"><HistoryView fixedVehicleId={v.id} /></TabsContent>
        <TabsContent value="playback"><PlaybackView fixedVehicleId={v.id} /></TabsContent>
        <TabsContent value="events"><EventsTab vid={v.id} /></TabsContent>
        <TabsContent value="reports"><ReportsTab vid={v.id} /></TabsContent>
        <TabsContent value="settings"><SettingsTab v={v} onSaved={load} /></TabsContent>
      </Tabs>
    </div>
  );
}
